'use client'

import { useStoryboard } from '../../context';
import PreviewFramePickerDialog from '../PreviewFramePickerDialog';
import { useSegmentTimelineState } from './useSegmentTimelineState';
import SegmentCard from './SegmentCard';
import AddSegmentButton from './AddSegmentButton';
import SegmentInfoPopover from './SegmentInfoPopover';
import PreviewContextMenu from './PreviewContextMenu';
import TimelineFeedbackSnackbar from './TimelineFeedbackSnackbar';

const SegmentTimeline: React.FC = () => {
  const storyboard = useStoryboard();
  const timeline = useSegmentTimelineState({
    segments: storyboard.segments,
    mutating: storyboard.mutating,
    activeProjectId: storyboard.activeProjectId,
    activeSegmentId: storyboard.activeSegmentId,
    createSegment: storyboard.createSegment,
    reorderSegments: storyboard.reorderSegments,
    deleteSegment: storyboard.deleteSegment,
    updateSegment: storyboard.updateSegment,
    selectSegment: storyboard.selectSegment,
    focusPreviewEntry: storyboard.focusPreviewEntry,
    focusedPreviewEntryIds: storyboard.focusedPreviewEntryIds,
  });

  const canDrag = storyboard.segments.length > 1;
  const hasActiveDrag = Boolean(timeline.dragState.draggingSegmentId);

  if (!storyboard.activeProjectId) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground min-h-[160px] md:min-h-[240px]">
        <div className="text-sm">请先从左侧选择或创建一个项目。</div>
      </div>
    )
  }

  return (
    <div className="min-h-[200px] flex flex-col gap-4 flex-1 bg-card/50 rounded-xl p-4">
      <div className="relative bg-card dark:bg-card rounded-lg p-4 border border-border">
        <div className="flex items-center gap-8 text-[10px] text-muted-foreground mb-3 border-b border-border pb-2 h-6">
          {['00:00', '00:05', '00:10', '00:15', '00:20', '00:25'].map((marker) => (
            <span key={marker}>{marker}</span>
          ))}
        </div>
        <div
          className="flex gap-4 items-stretch overflow-x-auto pb-1 -mr-1 pr-1 w-full bg-muted/30 rounded p-2 timeline-scrollbar"
          style={{
            justifyContent: timeline.visibleSegments.length === 0 ? 'center' : 'flex-start',
          }}
        >
          {timeline.visibleSegments.map((segment, index) => {
            const dropIndicator =
              timeline.dragState.dropTargetSegmentId === segment.segmentId
                ? timeline.dragState.dropPosition
                : null;
            return (
              <SegmentCard
                key={segment.segmentId}
                segment={segment}
                index={index}
                selected={segment.segmentId === storyboard.activeSegmentId}
                mutating={storyboard.mutating}
                draggableEnabled={canDrag}
                isDragging={timeline.dragState.draggingSegmentId === segment.segmentId}
                dropIndicator={dropIndicator}
                hasActiveDrag={hasActiveDrag}
                onSelect={timeline.handleSelectSegmentCard}
                onDelete={(segmentId) => {
                  void timeline.handleDeleteSegment(segmentId);
                }}
                onDragStart={timeline.handleDragStart}
                onDragEnd={timeline.handleDragEnd}
                onDragOver={timeline.handleDragOverSegment}
                onDragLeave={timeline.handleDragLeaveSegment}
                onDrop={(event, segmentId) => {
                  void timeline.handleDropOnSegment(event, segmentId);
                }}
              />
            );
          })}
          {timeline.visibleSegments.length < storyboard.segments.length ? (
            <div className="flex items-center justify-center min-w-[120px] text-muted-foreground">
              <div className="text-[11px]">加载更多单元…</div>
            </div>
          ) : null}
          <AddSegmentButton
            canAdd={Boolean(storyboard.activeProjectId)}
            mutating={storyboard.mutating}
            draggingOver={timeline.dragState.draggingOverAddButton}
            onAdd={(event) => {
              event.preventDefault();
              if (!storyboard.activeProjectId || storyboard.mutating) return;
              void timeline.handleCreateSegment();
            }}
            onDragOver={timeline.handleDragOverAddButton}
            onDragLeave={timeline.handleDragLeaveAddButton}
            onDrop={(event) => {
              void timeline.handleDropOnAddButton(event);
            }}
          />
        </div>
      </div>

      <SegmentInfoPopover
        open={Boolean(timeline.infoAnchorEl && timeline.selection.infoSegment)}
        anchorEl={timeline.infoAnchorEl}
        arrowRef={timeline.infoArrowRef}
        setArrowRef={timeline.setInfoArrowRef}
        onClose={timeline.handleCloseInfo}
        selection={timeline.selection}
        mutating={storyboard.mutating}
        onPreview={timeline.handlePreviewEntry}
        onAdopt={(entry) => {
          void timeline.handleAdoptPreview(entry);
        }}
        onOpenContextMenu={(entry, event) => {
          if (!timeline.selection.infoSegment) {
            return;
          }
          timeline.openPreviewContextMenu(
            entry,
            timeline.selection.infoSegment.segmentId,
            timeline.selection.infoSegment.projectId,
            event
          );
        }}
      />

      <PreviewFramePickerDialog
        open={Boolean(timeline.framePickerState)}
        entry={timeline.framePickerState?.entry ?? null}
        projectId={timeline.framePickerState?.projectId ?? null}
        segmentId={timeline.framePickerState?.sourceSegmentId ?? null}
        onClose={timeline.handleFramePickerClose}
        onSelect={(imageDataUrl) => {
          void timeline.handleFrameSelected(imageDataUrl);
        }}
      />

      <PreviewContextMenu
        state={timeline.previewContextMenu}
        onClose={timeline.closePreviewContextMenu}
        onSendRemix={() => {
          void timeline.handleSendRemixToNext();
        }}
        onRemixCurrent={() => {
          void timeline.handleRemixInCurrentSegment();
        }}
        onOpenFramePicker={timeline.handleOpenFramePicker}
        canSendToNext={timeline.hasNextSegmentForMenu && Boolean(timeline.contextMenuVideoId)}
        canRemixCurrent={timeline.canRemixInCurrentSegment && Boolean(timeline.contextMenuVideoId)}
        canPickFrame={timeline.hasNextSegmentForMenu && !timeline.isFramePickerBusy}
      />

      <TimelineFeedbackSnackbar feedback={timeline.feedback} />
    </div>
  );
};

export default SegmentTimeline;
